/**
 * `/claim` — bind a key you already hold to this account.
 *
 * The path for every vendor that is not us. Marketplaces and resellers hand
 * over a key and never tell us who bought it, so those licenses arrive with no
 * email attached; this screen is how they get one. It is also the fix for "I
 * bought from you but under my other address", which is more common than the
 * first case and much easier to say out loud.
 *
 * ── Every outcome is an answer, not an error ────────────────────────────────
 *
 * `not_found` and `claimed_by_other` come back as 200s carrying a discriminant,
 * because both are sentences somebody has to read and act on, and neither is a
 * failure of the request. Only a malformed key (400) and the throttle (429)
 * reject. That split is what lets this screen say five different, specific
 * things instead of one generic "something went wrong".
 *
 * ── On telling somebody their key belongs to another account ────────────────
 *
 * That answer makes this form an oracle for "does this key exist". It is a
 * considered trade, argued in `shared/customer_contract.ts`: it is worth almost
 * nothing against a 128-bit key space, and it is the difference between "check
 * your key" — which sends a customer to look at a receipt that is already
 * correct — and "this is registered to another account", which they can
 * actually resolve. The throttle that makes it safe is per-account and lives in
 * the Worker.
 */

import { PageHeader } from "@/components/PageHeader";
import { Marker, Notice, Strip, StripBody, StripHead } from "@/components/deck";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PortalError, portalApi, useApiMutation } from "@/lib/api";
import type { ClaimOutcome } from "@/lib/api";
import { type FormEvent, useState } from "react";
import { Link } from "react-router";
import { isPlausibleLicenseKey, normalizeLicenseKey } from "../../shared/portal";

export function ClaimRoute() {
  const [raw, setRaw] = useState("");
  const [outcome, setOutcome] = useState<ClaimOutcome | null>(null);

  const claim = useApiMutation((key: string) => portalApi.claimLicense(key), {
    // Both the outcomes and the failures are rendered in place, under the field
    // the customer is looking at.
    suppressToast: true,
    onSuccess: (result) => setOutcome(result),
  });

  const normalized = normalizeLicenseKey(raw);
  const plausible = isPlausibleLicenseKey(normalized);
  const tooShort = normalized.length > 0 && !plausible;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!plausible) return;
    setOutcome(null);
    claim.mutate(raw);
  }

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        code="ADD"
        label="Add a key"
        title={
          <>
            Add a <Marker draw>license key</Marker>
          </>
        }
        description="If you bought through a marketplace, or from us under a different email, paste the key here and it becomes part of this account."
      />

      <Strip>
        <StripHead
          title="License key"
          as="h2"
          id="claim-head"
          note="Copy it from your receipt or confirmation email. Spacing, hyphens and capitals do not matter."
        />
        <StripBody>
          <form onSubmit={onSubmit} noValidate>
            <label htmlFor="claim-key" className="t-field block">
              Key
            </label>
            <Input
              id="claim-key"
              name="key"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder="ALOFT-XXXX-XXXX-XXXX-XXXX"
              autoComplete="off"
              spellCheck={false}
              autoFocus
              required
              aria-invalid={tooShort || undefined}
              aria-describedby="claim-hint"
              className="mt-2 h-10 text-[1rem]"
            />

            {/* What will actually be looked up. Showing the normalized form is
                not decoration: a customer who pasted a stray line break or a
                zero-width character from a PDF receipt sees why a key that
                "looks right" is being rejected. */}
            <p id="claim-hint" className="mt-2 min-h-5 text-[0.8125rem] leading-snug text-ink-2">
              {normalized.length === 0 ? (
                "Nothing entered yet."
              ) : tooShort ? (
                <>That is only {normalized.length} characters — keys are longer than that.</>
              ) : (
                <>
                  We will look up <span className="t-data text-ink">{normalized}</span>
                </>
              )}
            </p>

            {claim.error ? <ClaimFailed error={claim.error} /> : null}

            <Button
              type="submit"
              size="lg"
              disabled={claim.isPending || !plausible}
              className="mt-5"
            >
              {claim.isPending ? "Checking…" : "Add this key"}
            </Button>
          </form>
        </StripBody>
      </Strip>

      {outcome ? <ClaimResult outcome={outcome} onRetry={() => setOutcome(null)} /> : null}
    </div>
  );
}

/** The two rejections. Both are already customer-readable sentences from the
 *  Worker; the only decision here is the tone. */
function ClaimFailed({ error }: { error: Error }) {
  const throttled = error instanceof PortalError && error.code === "rate_limited";
  return (
    <Notice
      tone={throttled ? "marker" : "hold"}
      label={throttled ? "Slow down" : "That key is not right"}
      className="mt-4"
    >
      {error.message}
    </Notice>
  );
}

function ClaimResult({ outcome, onRetry }: { outcome: ClaimOutcome; onRetry: () => void }) {
  switch (outcome.outcome) {
    case "claimed":
      return (
        <Notice
          tone="cleared"
          label="Added"
          className="mt-6"
          action={
            <Button asChild size="sm">
              <Link to={`/licenses/${outcome.license.id}`}>Open it</Link>
            </Button>
          }
        >
          Your {outcome.license.product_name} license is now on this account. It will be here
          whenever you sign in — you do not need to keep the receipt.
        </Notice>
      );

    case "already_yours":
      return (
        <Notice
          tone="cleared"
          label="Already yours"
          className="mt-6"
          action={
            <Button asChild variant="outline" size="sm">
              <Link to={`/licenses/${outcome.license.id}`}>Open it</Link>
            </Button>
          }
        >
          That key is already on this account — nothing to do.
        </Notice>
      );

    case "claimed_by_other":
      return (
        <Notice
          tone="marker"
          label="Registered elsewhere"
          className="mt-6"
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/support">Get help</Link>
            </Button>
          }
        >
          That key is registered to a different email address. If it is yours — a second address, or
          a gift — get in touch and we will move it across. We will not say whose it is.
        </Notice>
      );

    case "not_found":
      return (
        <Notice
          tone="marker"
          label="No match"
          className="mt-6"
          action={
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try another
            </Button>
          }
        >
          We have no license with that key. Check it against your receipt — the characters most
          often mistyped are 0 and O, and 1 and I. If it still will not take, forward us the receipt
          and we will sort it out.
        </Notice>
      );

    // `malformed` is answered as a 400 by the Worker before the RPC is reached,
    // so it arrives as `claim.error` and not here. Handled anyway, because the
    // contract can return it and a silently-ignored discriminant is a blank
    // screen the day that changes.
    default:
      return (
        <Notice tone="marker" label="Not a key" className="mt-6">
          That does not look like a Downwind license key.
        </Notice>
      );
  }
}
